import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';
import ParentUserPicker from '@/components/ParentUserPicker';
import SkudPersonPicker from '@/components/SkudPersonPicker';
import SkudPassesBlock from '@/components/SkudPassesBlock';

/**
 * Создание/редактирование контакта ученика.
 * Минимально-обязательно: фамилия + имя. Остальное — опционально.
 *
 * Если контакт назначается заказчиком в договоре, требование «всех полей» включается
 * в AgreementFormModal (там же есть форма дозаполнения недостающих полей).
 *
 *   mode='create' studentId={N}        → POST /api/contacts
 *   mode='edit'   contact={{...}}      → PUT  /api/contacts/{id}
 */
const EMPTY = {
  familyName: '',
  firstName: '',
  midName: '',
  role: '',
  sex: '',
  email: '',
  phone: '',
  passport: '',
  passportIssuedDate: '',
  passportIssuedBy: '',
  addressReg: '',
  addressFact: '',
  comment: '',
  isPrimary: false,
  isFinance: false,
  canPickup: false,
  archive: false,
  userId: null,
  createParentUser: false,
  sendInvite: true,
};

export default function ContactEditModal({ mode, studentId, contact, onClose, onSaved }) {
  const isEdit = mode === 'edit';
  const [data, setData] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isEdit && contact) {
      setData({
        familyName:         contact.familyName || '',
        firstName:          contact.firstName || '',
        midName:            contact.midName || '',
        role:               contact.role || '',
        sex:                contact.sex || '',
        email:              contact.email || '',
        phone:              contact.phone || '',
        passport:           contact.passport || '',
        passportIssuedDate: contact.passportIssuedDate || '',
        passportIssuedBy:   contact.passportIssuedBy || '',
        addressReg:         contact.addressReg || '',
        addressFact:        contact.addressFact || '',
        comment:            contact.comment || '',
        isPrimary:          !!contact.isPrimary,
        isFinance:          !!contact.isFinance,
        canPickup:          !!contact.canPickup,
        archive:            !!contact.archive,
        userId:             contact.userId || null,
        createParentUser:   false,
        sendInvite:         true,
      });
    } else {
      setData(EMPTY);
    }
  }, [isEdit, contact?.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      const payload = {
        ...data,
        familyName: data.familyName.trim(),
        firstName:  data.firstName.trim(),
        midName:    data.midName.trim(),
      };
      if (!isEdit) payload.studentId = studentId;
      const res = isEdit
        ? await api.put(`/api/contacts/${contact.id}`, payload)
        : await api.post('/api/contacts', payload);
      onSaved?.(res.contact);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sd-overlay" onClick={(e) => { if (e.target.classList.contains('sd-overlay')) onClose(); }}>
      <div className="sd-modal sd-modal--medium" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>{isEdit ? 'Контакт' : 'Новый контакт'}</h2>
          <button className="sd-close" onClick={onClose}><Icon name="x" size={14} /></button>
        </div>

        <form onSubmit={submit} className="sd-edit__form" noValidate>
          <div className="sd-edit__body">
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Существующий родитель</label>
              <ParentUserPicker
                value={data.userId}
                excludeStudentId={studentId || contact?.studentId}
                onPick={(user) => {
                  // Подтягиваем поля контакта из выбранного user. ФИО у
                  // users хранится одной строкой — режем по пробелам:
                  // первое слово → фамилия, второе → имя, остаток → отчество.
                  const parts = (user.name || '').trim().split(/\s+/);
                  setData((d) => ({
                    ...d,
                    userId:     user.id,
                    familyName: d.familyName || parts[0] || '',
                    firstName:  d.firstName  || parts[1] || '',
                    midName:    d.midName    || parts.slice(2).join(' ') || '',
                    email:      d.email      || user.email || '',
                    // Если выбрали существующего — создавать нового не нужно.
                    createParentUser: false,
                  }));
                }}
                onClear={() => set('userId', null)}
              />
              <div className="sd-edit__hint">
                Если этот же родитель уже привязан к другому ребёнку — выберите его
                здесь, чтобы система знала, что это один человек. Иначе оставьте поле
                пустым; ниже можно отметить «зарегистрировать как родителя», и система
                заведёт новую запись пользователя.
              </div>
              {!data.userId && (
                <>
                  <label className="ref-checkbox" style={{ marginTop: 6 }}>
                    <input
                      type="checkbox"
                      checked={data.createParentUser}
                      onChange={(e) => set('createParentUser', e.target.checked)}
                    />
                    <span>Зарегистрировать как родителя в системе</span>
                  </label>
                  {/* Чекбокс «Отправить приглашение» показываем, только когда
                      запрошена регистрация. Бэк автоматически сгенерирует
                      временный пароль и отправит письмо со ссылкой на ЛК.
                      Без email — бэк создаст учётку без пароля, письмо не
                      уйдёт; UI заранее об этом предупреждает. */}
                  {data.createParentUser && (
                    <label className="ref-checkbox" style={{ marginTop: 4, marginLeft: 24 }}>
                      <input
                        type="checkbox"
                        checked={!!data.sendInvite}
                        onChange={(e) => set('sendInvite', e.target.checked)}
                        disabled={!data.email}
                      />
                      <span>
                        Отправить письмо-приглашение на {data.email || '(укажите email выше)'}
                      </span>
                    </label>
                  )}
                </>
              )}
            </div>

            <div className="sd-edit__field">
              <label className="sd-edit__label">Фамилия *</label>
              <input className="sd-edit__input" required autoFocus
                value={data.familyName} onChange={(e) => set('familyName', e.target.value)} />
            </div>
            <div className="sd-edit__field">
              <label className="sd-edit__label">Имя *</label>
              <input className="sd-edit__input" required
                value={data.firstName} onChange={(e) => set('firstName', e.target.value)} />
            </div>
            <div className="sd-edit__field">
              <label className="sd-edit__label">Отчество</label>
              <input className="sd-edit__input"
                value={data.midName} onChange={(e) => set('midName', e.target.value)} />
            </div>
            <div className="sd-edit__field">
              <label className="sd-edit__label">Роль</label>
              <input className="sd-edit__input"
                value={data.role} onChange={(e) => set('role', e.target.value)}
                placeholder="например, Мама, Бабушка" />
            </div>

            <div className="sd-edit__field">
              <label className="sd-edit__label">Пол</label>
              <select className="sd-edit__select" value={data.sex} onChange={(e) => set('sex', e.target.value)}>
                <option value="">— не выбрано —</option>
                <option value="male">мужской</option>
                <option value="female">женский</option>
              </select>
            </div>
            <div className="sd-edit__field">
              <label className="sd-edit__label">Телефон</label>
              <input className="sd-edit__input" type="tel"
                value={data.phone} onChange={(e) => set('phone', e.target.value)} />
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Email</label>
              <input className="sd-edit__input" type="email"
                value={data.email} onChange={(e) => set('email', e.target.value)} />
            </div>

            <div className="sd-edit__field">
              <label className="sd-edit__label">Паспорт (серия и номер)</label>
              <input className="sd-edit__input"
                value={data.passport} onChange={(e) => set('passport', e.target.value)}
                placeholder="1234 567890" />
            </div>
            <div className="sd-edit__field">
              <label className="sd-edit__label">Дата выдачи</label>
              <input className="sd-edit__input" type="date"
                value={data.passportIssuedDate} onChange={(e) => set('passportIssuedDate', e.target.value)} />
            </div>
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Кем выдан</label>
              <input className="sd-edit__input"
                value={data.passportIssuedBy} onChange={(e) => set('passportIssuedBy', e.target.value)} />
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Адрес регистрации</label>
              <input className="sd-edit__input"
                value={data.addressReg} onChange={(e) => set('addressReg', e.target.value)} />
            </div>
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Адрес фактический</label>
              <input className="sd-edit__input"
                value={data.addressFact} onChange={(e) => set('addressFact', e.target.value)}
                placeholder="можно тот же, что регистрация" />
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Заметки</label>
              <input className="sd-edit__input"
                value={data.comment} onChange={(e) => set('comment', e.target.value)} />
            </div>

            <div className="sd-edit__field sd-edit__field--full" style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
              <label className="ref-checkbox">
                <input type="checkbox" checked={data.isPrimary}
                  onChange={(e) => set('isPrimary', e.target.checked)} />
                <span>основной</span>
              </label>
              <label className="ref-checkbox">
                <input type="checkbox" checked={data.isFinance}
                  onChange={(e) => set('isFinance', e.target.checked)} />
                <span>финансы</span>
              </label>
              <label className="ref-checkbox">
                <input type="checkbox" checked={data.canPickup}
                  onChange={(e) => set('canPickup', e.target.checked)} />
                <span>забирает</span>
              </label>
              {isEdit && (
                <label className="ref-checkbox">
                  <input type="checkbox" checked={data.archive}
                    onChange={(e) => set('archive', e.target.checked)} />
                  <span>в архиве</span>
                </label>
              )}
            </div>

            {/* СКУД-привязка живёт в users, не в contacts — поэтому
                блок показываем только если контакт привязан к user-родителю.
                Поле «Человек в СКУД» меняется через PUT /api/parents/{id},
                и сразу после привязки появляется блок с пропусками. */}
            {data.userId && isEdit && (
              <div className="sd-edit__field sd-edit__field--full">
                <label className="sd-edit__label">Человек в СКУД (на родителя)</label>
                <ParentSkudBlock userId={data.userId} />
              </div>
            )}

            <div className="sd-edit__field sd-edit__field--full">
              <div className="sd-edit__hint">
                Минимально обязательны фамилия и имя. Если этот контакт будет ставиться
                заказчиком договора — нужны также пол, телефон, email, паспорт (номер,
                дата, кем выдан), адреса регистрации и фактический. Без них печатная форма
                договора не соберётся.
              </div>
            </div>
          </div>

          <div className="sd-edit__foot">
            {error && <div className="sd-edit__error">{error}</div>}
            <div className="sd-edit__foot-spacer" />
            <button type="button" className="btn btn--sm" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn btn--primary btn--sm" disabled={saving}>
              {saving ? 'Сохраняем…' : (isEdit ? 'Сохранить' : 'Создать')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Блок «Человек в СКУД» для user-родителя + список его пропусков.
 * Подтягивает текущую привязку через GET /api/parents/{id}, меняет через
 * PUT /api/parents/{id} (поле skudPersonId).
 */
function ParentSkudBlock({ userId }) {
  const [parent, setParent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const reload = () =>
    api.get(`/api/parents/${userId}`)
      .then((res) => setParent(res.parent || null))
      .catch(() => setParent(null));

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [userId]);

  const onChange = async (p) => {
    if (saving) return;
    setSaving(true); setError(null);
    try {
      await api.put(`/api/parents/${userId}`, { skudPersonId: p?.skudId ?? null });
      await reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось сохранить привязку');
    } finally {
      setSaving(false);
    }
  };

  if (parent === null) return <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>Загрузка…</div>;

  const sp = parent.skudPerson;
  return (
    <>
      <SkudPersonPicker
        value={sp}
        role="parent"
        disabled={saving}
        onChange={onChange}
      />
      {error && <div style={{ color: 'var(--red-fg)', fontSize: 12.5, marginTop: 6 }}>{error}</div>}
      <div className="sd-edit__hint">
        Список фильтруется по подразделению «Родители» из настроек СКУД.
        Привязка глобальная — действует для всех детей этого родителя.
      </div>
      {sp && (
        <div style={{ marginTop: 10 }}>
          <SkudPassesBlock skudPersonId={sp.skudId} />
        </div>
      )}
    </>
  );
}
